import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';

import * as utils from '../../../UploadDocumentPage/utils';
import * as logger from '../../../../../logger';

import AddCardModal from '../AddCardModal/AddCardModal';

import { Button, Loader, Typography } from '@vivaahealth/design-system';
import {
  ActionContainer,
  Container,
  StyledCameraButton,
  StyledUploadButton,
  UploadedImage,
  UploadedImageContainer,
  UploadedImageContainerHeader,
} from './styles';
import { ReactComponent as CameraIcon } from '../../../../../assets/camera-icon-small-v2.svg';
import { ReactComponent as CardFront } from '../../../../../assets/card-front.svg';
import { ReactComponent as CardBack } from '../../../../../assets/card-back.svg';
import { ReactComponent as UploadIcon } from '../../../../../assets/upload.svg';
import { onKeyPress } from '../../../../../utils/keypress';
import { useMedia } from '../../../../../hooks/common/useMedia';
import { CaptureType, UploadError } from '../../types';
import { Side } from '../../../../../utils/enum';
import useDragAndDrop from '../../../../../hooks/common/useDragAndDrop';

const ACCEPTED_FILE_TYPES = [
  'image/png',
  'image/gif',
  'image/jpeg',
  'image/jpg',
  'application/pdf',
];
const UPLOAD_LOAD_TIME_MS = 500;

interface UploadCardSideProps extends WithTranslation {
  message: string;
  side: Side;
  uploadedBlob: Blob | null;
  onCapture: (blob: Blob | null, type: CaptureType) => void;
  onRemove: () => void;
  setCurrentError: Dispatch<SetStateAction<UploadError>>;
}

const UploadCardSide = ({
  message,
  setCurrentError,
  side,
  uploadedBlob,
  onCapture,
  onRemove,
  t,
}: UploadCardSideProps) => {
  const { isDesktop } = useMedia();
  const uploadButtonRef = useRef<HTMLInputElement>(null);
  const [isCaptureModalOpen, setIsCaptureModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const imgId = `card-${side.toLowerCase()}`;

  useEffect(() => {
    if (uploadedBlob !== null) {
      const urlCreator = window.URL || window.webkitURL;
      const imageUrl = urlCreator.createObjectURL(uploadedBlob);
      // @ts-expect-error
      document.querySelector(`#${imgId}`).src = imageUrl;
    }
  }, [imgId, uploadedBlob]);

  const onCaptureHandler = useCallback(
    (blob: Blob, type: CaptureType) => {
      setCurrentError(null);
      setIsUploading(true);
      setTimeout(() => {
        onCapture(blob, type);
        setIsUploading(false);
      }, UPLOAD_LOAD_TIME_MS);
    },
    [onCapture, setCurrentError],
  );

  const onFileUpload = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList || !fileList.length) {
        return;
      }

      let file: Blob = fileList[0];

      if (file.type === 'application/pdf') {
        try {
          file = await utils.convertPdfToImage(file);
        } catch (e) {
          logger.error(e, 'Unable to convert image from pdf to Blob');
          setCurrentError('pdf_conversion_failure');
          return;
        }
      } else if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        setCurrentError('incorrect_filetype');
        logger.error(
          new Error(`Unable to process image of type: ${file.type}`),
        );
        return;
      }

      onCaptureHandler(file, 'upload');
    },
    [onCaptureHandler, setCurrentError],
  );

  const { dragRef, dragging } = useDragAndDrop(onFileUpload);

  const CameraButton = (
    <StyledCameraButton
      size="small"
      fullWidth
      startIcon={<CameraIcon width={16} height={16} />}
      variant={isDesktop ? 'outlined' : 'primary'}
      onClick={() => setIsCaptureModalOpen(true)}
    >
      {t('capture', { ns: 'uploadCard' })}
    </StyledCameraButton>
  );

  // Don't use startIcon prop because then the icon is not part of the clickable label
  const UploadButton = (
    <StyledUploadButton
      component="label"
      fullWidth
      size="small"
      data-testid={`upload-${side.toLowerCase()}`}
      variant={isDesktop ? 'primary' : 'outlined'}
      onKeyDown={(e) =>
        onKeyPress({ e, onConfirm: () => uploadButtonRef.current?.click() })
      }
    >
      <input
        type="file"
        accept={ACCEPTED_FILE_TYPES.join(', ')}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onFileUpload(e.target.files)
        }
        ref={uploadButtonRef}
      />
      <UploadIcon />
      {t('upload', { ns: 'uploadCard' })}
    </StyledUploadButton>
  );

  if (uploadedBlob !== null) {
    return (
      <UploadedImageContainer>
        <UploadedImageContainerHeader>
          {side === Side.Front && (
            <Typography variant="title-medium" bold>
              {t('frontOfCard', { ns: 'uploadCard' })}
            </Typography>
          )}
          {side === Side.Back && (
            <Typography variant="title-medium" bold>
              {t('backOfCard', { ns: 'uploadCard' })}
            </Typography>
          )}
          <Button onClick={onRemove} variant="text" size="small">
            {t('remove', { ns: 'uploadCard' })}
          </Button>
        </UploadedImageContainerHeader>
        <UploadedImage
          alt={t(side === Side.Front ? 'imgAltFront' : 'imgAltBack')}
          id={imgId}
        />
      </UploadedImageContainer>
    );
  }

  if (isUploading) {
    return (
      <Container>
        <Loader label={t('loaderLabel', { ns: 'uploadCard' })} />
      </Container>
    );
  }

  return (
    <>
      <Container ref={dragRef as any} dragging={dragging}>
        {side === Side.Back ? <CardFront /> : <CardBack />}
        <Typography variant="title-medium" bold>
          {message}
        </Typography>
        <ActionContainer isDesktop={isDesktop}>
          {UploadButton}
          {CameraButton}
        </ActionContainer>
      </Container>
      {isCaptureModalOpen && (
        <AddCardModal
          onClose={() => setIsCaptureModalOpen(false)}
          currentSide={side}
          onCapture={onCaptureHandler}
        />
      )}
    </>
  );
};

export default withTranslation()(UploadCardSide);
